import {initializeApp, getApps, FirebaseApp} from 'firebase/app';
import {
  getAuth,
  Auth,
  signInWithPopup,
  TwitterAuthProvider,
} from 'firebase/auth';
import {getFunctions, Functions, httpsCallable, connectFunctionsEmulator} from 'firebase/functions';
import {initializeAppCheck, ReCaptchaV3Provider} from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Check if emulators should be used
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';

let app: FirebaseApp;
let auth: Auth;
let functions: Functions;

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

auth = getAuth(app);
functions = getFunctions(app, 'us-central1');

// Initialize App Check
// In development, enable debug mode to get a debug token
if (USE_EMULATORS) {
  // @ts-expect-error - Debug token for local development
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

// Initialize App Check with reCAPTCHA v3
// You need to add VITE_RECAPTCHA_SITE_KEY to your .env file
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (recaptchaSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Connect to emulators in development (except Auth - always use live)
if (USE_EMULATORS) {
  console.log('🔧 Using Firebase Emulators (Auth uses live)');
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

export {app, auth, functions, USE_EMULATORS};

// Twitter authentication
const twitterProvider = new TwitterAuthProvider();

export const signInWithTwitter = async (): Promise<import('firebase/auth').User> => {
  const result = await signInWithPopup(auth, twitterProvider);
  return result.user;
};

// Callable functions
import type { UserProfile, AgentProfile } from '@molt-starter/shared';

// User details (includes agentName if claimed)
export const callGetUserDetails = httpsCallable<void, UserProfile>(
  functions, 
  'getUserDetails'
);

// API key regeneration
export const callRegenerateApiKey = httpsCallable<
    void,
    { apiKey: string }
>(functions, 'regenerateApiKey');

// Get agent for claim (shows verification code before claiming)
export const callGetAgentForClaim = httpsCallable<
    { claimToken: string },
    AgentProfile
>(functions, 'getAgentForClaim');

// Agent claim
export const callClaimAgent = httpsCallable<
    { claimToken: string },
    { success: boolean; id: string; name: string; verificationCode: string }
>(functions, 'claimAgent');
